import { useEffect, useState } from 'react';
import { importarDoErp } from '../../api/depositos.js';
import type { ImportacaoDoErp } from '../../api/depositos.js';
import { mensagemDoErro } from '../../api/erros.js';
import { obterIntegracao } from '../../api/integracao-erp.js';
import type { TomDoAviso } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { IconeAtualizar } from '../../componentes/Icones.js';

export type AvisoDaImportacao = { readonly tom: TomDoAviso; readonly texto: string };

type Props = {
  /** Importou: o aviso já vem redigido, e a lista precisa ser recarregada. */
  readonly aoImportar: (aviso: AvisoDaImportacao) => void;
  /** Não importou: nada foi gravado. */
  readonly aoFalhar: (texto: string) => void;
};

/**
 * "Sincronizar do ERP" — só aparece com a integração LIGADA.
 *
 * Quem renderiza este componente já conferiu o perfil (só o Administrador
 * sincroniza); aqui fica a outra metade da condição. Integração desligada, não
 * configurada ou ilegível escondem o botão sem mostrar erro: a tela é de
 * consulta de depósitos, e um aviso sobre a integração no topo dela seria
 * ruído para quem só veio conferir uma filial.
 */
export function ImportarDoErp({ aoImportar, aoFalhar }: Props) {
  const [integracaoLigada, definirIntegracaoLigada] = useState(false);
  const [confirmando, definirConfirmando] = useState(false);
  const [agindo, definirAgindo] = useState(false);

  useEffect(() => {
    let montado = true;

    obterIntegracao()
      .then((configuracao) => {
        if (montado) {
          definirIntegracaoLigada(configuracao.ativo);
        }
      })
      .catch(() => {
        if (montado) {
          definirIntegracaoLigada(false);
        }
      });

    return () => {
      montado = false;
    };
  }, []);

  if (!integracaoLigada) {
    return null;
  }

  function importar() {
    definirAgindo(true);

    importarDoErp()
      .then((resultado) => {
        definirAgindo(false);
        definirConfirmando(false);
        aoImportar(avisoDaImportacao(resultado));
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirConfirmando(false);
        aoFalhar(mensagemDoErro(falha));
      });
  }

  return (
    <>
      <Botao
        altura="media"
        onClick={() => {
          definirConfirmando(true);
        }}
      >
        <IconeAtualizar tamanho={15} />
        Sincronizar do ERP
      </Botao>

      {confirmando && (
        <DialogoConfirmacao
          titulo="Sincronizar os depósitos do ERP?"
          rotuloConfirmar="Sincronizar"
          variante="primario"
          agindo={agindo}
          aoConfirmar={importar}
          aoCancelar={() => {
            definirConfirmando(false);
          }}
        >
          Os depósitos de cada filial do Senior serão gravados aqui, e os coletores baixam a lista
          nova na próxima sincronização. Depósito que sumiu do ERP fica inativo — nada é apagado, e
          nada é escrito no ERP.
        </DialogoConfirmacao>
      )}
    </>
  );
}

function plural(quantidade: number, singular: string, pluralizado: string): string {
  return `${String(quantidade)} ${quantidade === 1 ? singular : pluralizado}`;
}

/**
 * O resultado em uma frase — e em tom de atenção quando algo ficou de fora.
 *
 * Filial que falhou não desfaz o resto: o aviso diz o que entrou E o que não
 * entrou, para ninguém concluir que o ERP inteiro foi lido.
 */
export function avisoDaImportacao(resultado: ImportacaoDoErp): AvisoDaImportacao {
  const { resumo } = resultado;
  const partes = [
    `Sincronização com o ERP concluída: ${plural(resumo.novos, 'depósito novo', 'depósitos novos')}, ` +
      `${plural(resumo.atualizados, 'atualizado', 'atualizados')}, ` +
      `${plural(resumo.inativados, 'inativado', 'inativados')}, ` +
      `${plural(resumo.inalterados, 'inalterado', 'inalterados')}.`,
  ];

  if (resultado.descartados.length > 0) {
    partes.push(
      `${plural(resultado.descartados.length, 'registro do ERP ignorado', 'registros do ERP ignorados')} ` +
        '(depósito virtual, que não aceita produto ou com código inválido).',
    );
  }

  for (const falha of resultado.filiaisComFalha) {
    partes.push(
      `Não foi possível ler a filial ${String(falha.codFil)}: ${falha.motivo} — ` +
        'os depósitos dela ficaram como estavam.',
    );
  }

  partes.push(...resultado.avisos.map((aviso) => `Aviso do ERP: ${aviso}.`));

  const algoFicouDeFora = resultado.filiaisComFalha.length > 0 || resultado.avisos.length > 0;

  return { tom: algoFicouDeFora ? 'atencao' : 'ok', texto: partes.join(' ') };
}
